/**
 * Stripe Checkout funnel state.
 *
 * The server records `checkout_started` after session creation; the browser
 * then fires pixels and stashes join metadata in sessionStorage keyed by
 * `groupId`. If the user
 * lands back on the cancel_url (`?canceled=1`), the Pricing page reads
 * the entry back and fires `checkout_abandoned` with the same metadata for
 * a clean join in the admin funnel.
 *
 * sessionStorage (not localStorage) is intentional — Stripe Checkout opens
 * in a new tab from PWA installs, but redirect-back lands in the same tab
 * 99% of the time. Persisting across tabs would require localStorage with
 * its own cleanup nightmare; the per-tab session model is the right scope.
 */

const KEY_PREFIX = 'wk:pendingCheckout:'

/**
 * Which product the abandoned session was for. Subscriptions and the one-time
 * Trip Pass land on the SAME cancel URL, so without this the cancel path
 * reported every abandoned pass as a generic subscription abandonment with
 * `feature: 'pricing_page'`. Absent in entries written before this field
 * existed — treat a missing value as 'subscription'.
 */
export type PendingCheckoutKind = 'subscription' | 'trip_pass'

export interface PendingCheckoutMeta {
  /** Product class. Defaults to 'subscription' when absent (legacy entries). */
  kind?: PendingCheckoutKind
  /** 'plus' | 'pro' for subscriptions, 'trip_pass' for a pass. */
  target_tier: string
  /** 'monthly' | 'annual' for subscriptions, 'one_time' for a pass. */
  cadence: string
  /** Source paywall slug (smart_packing, flight_prices, …). */
  feature: string
  /** Originating Stripe Checkout session_id, when available. */
  session_id?: string
  /** Browser-generated UUID joining request, API outcome, and Stripe session. */
  request_id?: string
  /** The price shown on the pricing card CTA at click time, in cents — lets
   *  `checkout_abandoned` distinguish price shock from a broken page instead
   *  of landing with no price context at all. */
  price_cents?: number
  /** Currency for `price_cents`, e.g. 'USD'. */
  currency?: string
  /** Ms epoch — used to age out stale entries on cancel landing. */
  startedAt: number
}

/** Stash the start metadata so `checkout_abandoned` can read it back. */
export function setPendingCheckout(groupId: string, meta: PendingCheckoutMeta): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.setItem(KEY_PREFIX + groupId, JSON.stringify(meta))
  } catch {
    // sessionStorage unavailable (private mode, quota) — fall through; the
    // abandoned event will fire without metadata, which is still a useful signal.
  }
}

/** Read the pending entry without removing it. */
export function getPendingCheckout(groupId: string): PendingCheckoutMeta | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(KEY_PREFIX + groupId)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<PendingCheckoutMeta>
    if (typeof parsed.target_tier !== 'string') return null
    if (typeof parsed.cadence !== 'string') return null
    if (typeof parsed.feature !== 'string') return null
    if (typeof parsed.startedAt !== 'number') return null
    return {
      kind: parsed.kind === 'trip_pass' ? 'trip_pass' : 'subscription',
      target_tier: parsed.target_tier,
      cadence: parsed.cadence,
      feature: parsed.feature,
      session_id: typeof parsed.session_id === 'string' ? parsed.session_id : undefined,
      request_id: typeof parsed.request_id === 'string' ? parsed.request_id : undefined,
      price_cents: typeof parsed.price_cents === 'number' ? parsed.price_cents : undefined,
      currency: typeof parsed.currency === 'string' ? parsed.currency : undefined,
      startedAt: parsed.startedAt,
    }
  } catch {
    return null
  }
}

/** Remove the entry — call on a fresh start re-fire and on success. */
export function clearPendingCheckout(groupId: string): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.removeItem(KEY_PREFIX + groupId)
  } catch {
    // Best-effort.
  }
}
